import React, { useEffect, useMemo, useState } from "react";
import { useAccountCache } from "../hooks/useAccountCache";
import { ordersApi } from "../services/orders";
import type { MonthlyEarnings, CurrencyBucket } from "../types/orders";
import { formatTxnAmount } from "../utils/currency";
import "./MonthlyEarningsCard.css";
import { t, currentLanguage } from "../i18n";

// "Earned this month" — Revolut's "Spent this month" card, but earnings
// (founder brief). Sits directly below the recent-activity bubble
// on the Requests page: the bubble says what just happened, this says how the
// month is going. Purpose is for talent to track and chase their earnings.
//
// Data: GET /orders/monthly-earnings — per-day played gross for this month and
// last, per currency, bucketed in the talent's own timezone. Its totals are
// the SAME played-only source as the Balance tab's earnings figure, so the
// number on this card can never disagree with the one on Balance.
//
// Tapping the card expands an "Insights" panel. Every stat in it is derived
// from the payload already fetched — no second request, no second endpoint.
// It expands IN PLACE rather than opening a sheet: an overlay here would
// inherit the iOS synthesized ghost-click problem that bit the history modal
// once, and there's nothing here worth a backdrop.

interface Props {
  /** Bump to refetch — the page passes the same counter the bubble uses, so
      playing a request updates both cards at once. */
  refreshKey?: number;
}

// Month names in the app's language (English: "January", as before).
const monthName = (m: number) =>
  new Date(Date.UTC(2000, (m - 1) % 12, 1)).toLocaleDateString(currentLanguage(), { month: "long", timeZone: "UTC" });

// Running totals for days 1..upto.
function cumulative(days: number[], upto: number): number[] {
  const out: number[] = [];
  let sum = 0;
  for (let i = 0; i < upto; i++) {
    sum += days[i] || 0;
    out.push(sum);
  }
  return out;
}

// The big number must never wrap or overflow its card — a HUF talent's
// "1,250,000 Ft" is 3x the width of "$40.00". Step the type down instead of
// letting the layout move (the card's height is fixed at every state).
function sizeClass(text: string): string {
  if (text.length >= 14) return "mec-total--xs";
  if (text.length >= 11) return "mec-total--sm";
  return "";
}

const CHART_W = 300;
const CHART_H = 92;
const PAD_L = 2;
const PAD_R = 2;

const MonthlyEarningsCard: React.FC<Props> = ({ refreshKey = 0 }) => {
  // Last month's card paints at once on a later visit (display only — no
  // money moves from here); the skeleton is for the very first open.
  const [data, setData, fromCache] = useAccountCache<MonthlyEarnings | null>("reqs-monthly", null);
  const [loading, setLoading] = useState(!fromCache);
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // The server cuts month boundaries at midnight in THIS clock, so a 1am
    // play counts toward the night it felt like.
    ordersApi
      .monthlyEarnings(new Date().getTimezoneOffset())
      .then((res) => {
        if (!alive) return;
        setData(res || null);
      })
      .catch(() => {
        /* keeps what is shown (nothing on a first open) */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, setData]);

  // Currencies worth showing: any with money in either month, biggest first.
  // Chips only ever appear for a talent paid in more than one — for everyone
  // else this collapses to a single entry and never renders.
  const currencies = useMemo(() => {
    if (!data) return [];
    const own = data.currency;
    const ranked = Object.entries(data.byCurrency)
      .filter(([, b]) => b.total > 0 || b.prevTotal > 0)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([c]) => c);
    if (ranked.length === 0) return [own];
    // Own currency leads when it has money of its own. When it doesn't, lead
    // with the money — otherwise a talent paid in something other than their
    // setting opens to a zeroed card with their real earnings hidden.
    return ranked.includes(own) ? [own, ...ranked.filter((c) => c !== own)] : ranked;
  }, [data]);

  const active = cur && currencies.includes(cur) ? cur : currencies[0] || "usd";
  const bucket: CurrencyBucket | null = data?.byCurrency?.[active] || null;

  const view = useMemo(() => {
    if (!data || !bucket) return null;
    const { days: monthDays, today } = data.month;

    // The line runs day 1 → today. The server derives `today` from the same
    // offset it bucketed the days with, so there is never a bucket past it.
    const drawTo = Math.min(Math.max(today, 1), monthDays);
    const thisCum = cumulative(bucket.days, drawTo);
    const prevCum = cumulative(bucket.prevDays, bucket.prevDays.length);

    const peak = Math.max(
      thisCum[thisCum.length - 1] || 0,
      prevCum[prevCum.length - 1] || 0
    );
    // Both lines share one y-axis — that shared scale IS the comparison.
    // 15% headroom so the winning line never kisses the ceiling and the head
    // dot always has room to sit above it.
    const maxY = peak > 0 ? peak * 1.15 : 1;

    const innerW = CHART_W - PAD_L - PAD_R;
    const span = Math.max(monthDays - 1, 1);
    const x = (i: number) => PAD_L + (Math.min(i, span) / span) * innerW;
    const y = (v: number) => CHART_H - (v / maxY) * CHART_H;
    const toPath = (pts: number[]) =>
      pts.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

    const total = bucket.total || 0;
    const prevToDate = bucket.prevToDateTotal || 0;
    const delta = total - prevToDate;
    // Percentage needs something to divide by. Against a zero month it's
    // infinite, so show the money instead — "▲ $190.50" beats "▲ ∞%".
    const pct = prevToDate > 0 ? Math.round((delta / prevToDate) * 100) : null;

    const activeDays = bucket.days.filter((d) => d > 0).length;
    const bestIdx = bucket.days.reduce(
      (best, v, i) => (v > (bucket.days[best] ?? 0) ? i : best),
      0
    );

    return {
      thisPath: toPath(thisCum),
      prevPath: prevCum.length > 1 ? toPath(prevCum) : "",
      headX: x(thisCum.length - 1),
      headY: y(thisCum[thisCum.length - 1] || 0),
      hasMoney: peak > 0,
      total,
      delta,
      pct,
      prevToDate,
      activeDays,
      bestDay: bucket.days[bestIdx] > 0 ? { day: bestIdx + 1, amount: bucket.days[bestIdx] } : null,
      monthDays,
      today,
    };
  }, [data, bucket]);

  if (loading) {
    return (
      <section className="mec-card mec-card--skeleton" aria-busy="true">
        <div className="mec-skel mec-skel--label" />
        <div className="mec-skel mec-skel--total" />
        <div className="mec-skel mec-skel--chart" />
      </section>
    );
  }

  // Ambient content — a failed fetch shows nothing rather than an error card
  // (a failed refresh keeps the card the talent already sees).
  if (!data || !bucket || !view) return null;

  const totalText = formatTxnAmount(view.total, active);
  const thisMonth = monthName(data.month.month);
  const prevMonth = monthName(data.prevMonth.month);

  return (
    <section className="mec-card" aria-label={t("monthlyEarningsCard.earnedIn", { thisMonth })}>
      <button
        type="button"
        className="mec-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="mec-head-text">
          <div className="mec-label">{t("monthlyEarningsCard.earnedThisMonth")}</div>
          <div className={`mec-total ${sizeClass(totalText)}`}>{totalText}</div>

          {/* A month at zero never gets a percentage. "▼100% vs last month"
              is technically true and reads like a telling-off — and this card
              exists to make earning feel worth chasing. The dotted line is
              already showing them the target. */}
          {view.total > 0 && view.delta === 0 ? (
            // Dead level. "—0% vs last month" is an arrow, a zero and a
            // comparison all fighting to say one thing.
            <div className="mec-delta mec-delta--flat">{t("monthlyEarningsCard.levelWithLastMonth")}</div>
          ) : view.total > 0 ? (
            <div
              className={`mec-delta ${view.delta > 0 ? "mec-delta--up" : "mec-delta--down"}`}
            >
              <span className="mec-arrow" aria-hidden="true">
                {view.delta > 0 ? "▲" : "▼"}
              </span>
              {view.pct !== null
                ? `${Math.abs(view.pct)}%`
                : formatTxnAmount(Math.abs(view.delta), active)}
              <span className="mec-delta-tail">{t("monthlyEarningsCard.vsLastMonth")}</span>
            </div>
          ) : (
            <div className="mec-delta mec-delta--flat">
              {view.hasMoney ? t("monthlyEarningsCard.nothingYetThisMonth") : t("monthlyEarningsCard.yourFirstPlayLands")}
            </div>
          )}
        </div>
        <span className={`mec-chevron ${open ? "mec-chevron--open" : ""}`} aria-hidden="true" />
      </button>

      <div className="mec-chart-wrap">
        <svg
          className="mec-chart"
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={
            view.total > 0
              ? t("monthlyEarningsCard.earnedSoFarIn", { total: totalText, thisMonth })
              : t("monthlyEarningsCard.noEarningsYetIn", { thisMonth })
          }
        >
          {/* Last month, same y-axis — the dotted line IS the target to beat. */}
          {view.prevPath && (
            <path className="mec-line-prev" d={view.prevPath} vectorEffect="non-scaling-stroke" />
          )}
          <path className="mec-line" d={view.thisPath} vectorEffect="non-scaling-stroke" />
        </svg>

        {/* Marks where this month's line has got to. It sits OUTSIDE the SVG:
            preserveAspectRatio="none" stretches the viewBox to the card width,
            which would squash a circle drawn inside it into an ellipse.
            Gated on this month's own money, so a talent who earned last month
            and nothing yet doesn't get a green dot sitting on zero. */}
        {view.total > 0 && (
          <span
            className="mec-head-dot"
            style={{
              left: `${(view.headX / CHART_W) * 100}%`,
              top: `${(view.headY / CHART_H) * 100}%`,
            }}
            aria-hidden="true"
          />
        )}
      </div>

      <div className="mec-axis" aria-hidden="true">
        <span>1</span>
        <span>{Math.round(view.monthDays / 2)}</span>
        <span>{view.monthDays}</span>
      </div>

      {open && (
        <div className="mec-insights">
          <div className="mec-stat">
            <span className="mec-stat-k">{t("monthlyEarningsCard.requestsPlayed")}</span>
            <span className="mec-stat-v">{bucket.count}</span>
          </div>
          <div className="mec-stat">
            <span className="mec-stat-k">{t("monthlyEarningsCard.averageRequest")}</span>
            <span className="mec-stat-v">
              {bucket.count > 0 ? formatTxnAmount(view.total / bucket.count, active) : "—"}
            </span>
          </div>
          <div className="mec-stat">
            <span className="mec-stat-k">{t("monthlyEarningsCard.bestDay")}</span>
            <span className="mec-stat-v">
              {view.bestDay
                ? `${thisMonth.slice(0, 3)} ${view.bestDay.day} · ${formatTxnAmount(
                    view.bestDay.amount,
                    active
                  )}`
                : "—"}
            </span>
          </div>
          <div className="mec-stat">
            <span className="mec-stat-k">{t("monthlyEarningsCard.nightsEarning")}</span>
            <span className="mec-stat-v">
              {t("monthlyEarningsCard.of", { activeDays: view.activeDays, today: view.today })}
            </span>
          </div>
          <div className="mec-stat mec-stat--full">
            <span className="mec-stat-k">{t("monthlyEarningsCard.allOf", { prevMonth })}</span>
            <span className="mec-stat-v">{formatTxnAmount(bucket.prevTotal, active)}</span>
          </div>

          {/* Multi-currency talent: one card per currency, never a blended
              total. Hidden entirely for the single-currency majority. */}
          {currencies.length > 1 && (
            <div className="mec-curs">
              {currencies.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`mec-cur ${c === active ? "mec-cur--on" : ""}`}
                  onClick={() => setCur(c)}
                >
                  {c.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default MonthlyEarningsCard;
